// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/financeiro/services/ativoFixoService.ts
//  Ativo Fixo (Imobilizado): cadastro · baixa · processamento de depreciação.
// ════════════════════════════════════════════════════════════════════════════

import { apiClient } from '@/lib/apiClient';
import type { PaginadoResponse } from '../types';

// ── Tipos ───────────────────────────────────────────────────────────────────

export const SITUACAO_ATIVO_FIXO = {
  ATIVO: 1,
  BAIXADO: 2,
  TOTALMENTE_DEPRECIADO: 3,
} as const;

export interface FiltroAtivosFixos {
  /** Descrição, patrimônio, localização ou nº da nota. */
  busca?: string;
  situacao?: number;
  /** Aquisição de/até (yyyy-MM-dd). */
  dtInicio?: string;
  dtFim?: string;
  pagina?: number;
  tamanhoPagina?: number;
}

export interface AtivoFixoResumo {
  id: number;
  descricao: string;
  patrimonio?: string | null;
  dataAquisicao: string;
  valorAquisicao: number;
  valorResidual: number;
  vidaUtilMeses: number;
  depreciacaoAcumulada: number;
  valorContabilLiquido: number;
  situacao: number;
  situacaoDescricao: string;
  localizacao?: string | null;
  dataBaixa?: string | null;
}

export interface AtivoFixoDepreciacao {
  id: number;
  ativoFixoId: number;
  competencia: string;
  valor: number;
  valorAcumulado: number;
  valorContabilLiquido: number;
  dataProcessamento: string;
  isEstornado: boolean;
}

export interface AtivoFixoDetalhe {
  id: number;
  descricao: string;
  patrimonio?: string | null;
  dataAquisicao: string;
  dataInicioDepreciacao: string;
  valorAquisicao: number;
  valorResidual: number;
  vidaUtilMeses: number;
  metodoDepreciacao: number;
  depreciacaoAcumulada: number;
  valorContabilLiquido: number;
  depreciacaoMensal: number;
  situacao: number;
  situacaoDescricao: string;
  contaContabilId: number;
  centroCustoId: number;
  fornecedorId: number;
  numeroNota?: string | null;
  localizacao?: string | null;
  dataBaixa?: string | null;
  valorBaixa?: number | null;
  motivoBaixa?: string | null;
  observacao?: string | null;
  dtCriacao: string;
  depreciacoes: AtivoFixoDepreciacao[];
}

export interface CriarAtivoFixoRequest {
  descricao: string;
  dataAquisicao: string;
  valorAquisicao: number;
  vidaUtilMeses: number;
  valorResidual?: number;
  dataInicioDepreciacao?: string;
  metodoDepreciacao?: number;
  patrimonio?: string;
  contaContabilId?: number;
  centroCustoId?: number;
  fornecedorId?: number;
  numeroNota?: string;
  localizacao?: string;
  observacao?: string;
}

export interface AtualizarAtivoFixoRequest {
  descricao?: string;
  valorResidual?: number;
  vidaUtilMeses?: number;
  contaContabilId?: number;
  centroCustoId?: number;
  fornecedorId?: number;
  patrimonio?: string;
  numeroNota?: string;
  localizacao?: string;
  observacao?: string;
}

export interface BaixarAtivoFixoRequest {
  dataBaixa: string;
  motivo: string;
  valorBaixa?: number;
}

export interface ProcessarDepreciacaoRequest {
  ano: number;
  mes: number;
  ativoFixoId?: number;
}

export interface ProcessarDepreciacaoResponse {
  competencia: string;
  ativosProcessados: number;
  ativosIgnorados: number;
  totalDepreciado: number;
}

// ── Serviço ───────────────────────────────────────────────────────────────

const BASE = '/api/ativos-fixos';

export const ativoFixoService = {
  async getAll(filtro: FiltroAtivosFixos = {}): Promise<AtivoFixoLista> {
    const { data } = await apiClient.get<AtivoFixoLista>(BASE, {
      params: {
        busca: filtro.busca?.trim() || undefined,
        situacao: filtro.situacao || undefined,
        dtInicio: filtro.dtInicio || undefined,
        dtFim: filtro.dtFim || undefined,
        pagina: filtro.pagina ?? 1,
        tamanhoPagina: filtro.tamanhoPagina ?? 20,
      },
    });
    return data;
  },

  async getById(id: number): Promise<AtivoFixoDetalhe> {
    const { data } = await apiClient.get<AtivoFixoDetalhe>(`${BASE}/${id}`);
    return data;
  },

  async create(request: CriarAtivoFixoRequest): Promise<AtivoFixoDetalhe> {
    const { data } = await apiClient.post<AtivoFixoDetalhe>(BASE, request);
    return data;
  },

  async update(id: number, request: AtualizarAtivoFixoRequest): Promise<AtivoFixoDetalhe> {
    const { data } = await apiClient.put<AtivoFixoDetalhe>(`${BASE}/${id}`, request);
    return data;
  },

  async remove(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/${id}`);
  },

  async baixar(id: number, request: BaixarAtivoFixoRequest): Promise<AtivoFixoDetalhe> {
    const { data } = await apiClient.post<AtivoFixoDetalhe>(`${BASE}/${id}/baixar`, request);
    return data;
  },

  async processarDepreciacao(request: ProcessarDepreciacaoRequest): Promise<ProcessarDepreciacaoResponse> {
    const { data } = await apiClient.post<ProcessarDepreciacaoResponse>(`${BASE}/processar-depreciacao`, request);
    return data;
  },
};

/** Página de ativos + totais do FILTRO INTEIRO (os KPIs da tela). */
export type AtivoFixoLista = PaginadoResponse<AtivoFixoResumo> & {
  totais: { aquisicao: number; depreciacaoAcumulada: number; liquido: number };
};
